import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanelGroup, DataPanelItem, NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import type { InvolvedAgencyStateInfo } from '@ngsos/portal-core/incidents';
import { InvolvedAgencyCollaborationStateBadge } from '../involved-agencies/InvolvedAgencyCollaborationStateBadge';

export type InvolvedAgenciesDetailDialogHandle = {
  show: (incidentInvolvedAgencyStates: InvolvedAgencyStateInfo[]) => void;
};

export type InvolvedAgenciesDetailDialogProps = {
  ref?: Ref<InvolvedAgenciesDetailDialogHandle>;
};

function InvolvedAgenciesDetailDialogItem({ involvedAgency }: { involvedAgency: InvolvedAgencyStateInfo }) {
  return (
    <div className="list-group-item px-0">
      <div className="d-flex justify-content-between align-items-center gap-2">{involvedAgency.agencyName}</div>
      <div className="col">
        {involvedAgency.stateHistory.map((record) => (
          <InvolvedAgencyCollaborationStateBadge key={`${record.changedAtUtc}-${record.state}`} currentState={record} />
        ))}
      </div>
    </div>
  );
}

export function InvolvedAgenciesDetailDialog({ ref }: InvolvedAgenciesDetailDialogProps) {
  const { t } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);
  const [agencyStates, setAgencyStates] = useState<InvolvedAgencyStateInfo[]>([]);

  useImperativeHandle(ref, () => ({
    show: (incidentInvolvedAgencyStates) => {
      setAgencyStates(incidentInvolvedAgencyStates);
      modalRef.current?.show();
    },
  }));

  return (
    <NgModal
      ref={modalRef}
      header={t('Incident_InvolvedAgencies_DialogTitle')}
      bodyTemplate={
        <DataPanelGroup>
          <DataPanelItem>
            <div className="list-group list-group-flush">
              {agencyStates.map((item) => (
                <InvolvedAgenciesDetailDialogItem key={item.agencyId} involvedAgency={item} />
              ))}
            </div>
          </DataPanelItem>
        </DataPanelGroup>
      }
      footerTemplate={<NgButton text={t('Button_Close')} onClick={() => modalRef.current?.hide()} />}
    />
  );
}
